import { FormEvent, Component } from 'react';
import track from 'react-tracking';
import { Textfield } from '@simplywallst/ui-core';
import { Dropdown } from '@components/Dropdown';
import { withTheme } from 'styled-components';
import type { WithThemeProps } from '@simplywallst/ui-core';
import {
  TRANSACTION_TYPE_BUY,
  TRANSACTION_TYPE_SELL,
} from '@/components/DeprecatedPortfolio/redux/constants';
import { AddTransactionField, FieldWrapper } from './styled';
import { precisionRound } from '@utilities/formatting';
import { toSignificant } from '@simplywallst/formatting';
import { Row } from '@simplywallst/ui-core';
import Modal from '@components/Modal';
import Media from 'react-media';
import DateField from '@components/DateField';
import { get, isNumber, isNaN, isEqualWith } from 'lodash';
import { isEqual, isValid, isFuture } from 'date-fns';
import { superstruct } from 'superstruct';

export interface FetchPriceArgs {
  itemId?: string;
  companyId?: string;
  requestDate: Date;
}

const structTypes = superstruct({
  types: {
    number: (value, branch, path) => {
      let error = '';
      const number: number =
        typeof value === 'string' ? parseFloat(value) : value;
      if (!error && !isNumber(number)) {
        error = 'Please enter a number';
      }
      if (!error && isNaN(number)) {
        error = 'Please enter a number';
      }
      if (!error && number < 0) {
        error = 'Please enter a positive number';
      }
      if (error) {
        return {
          type: 'number',
          value,
          message: error,
          path,
          branch,
        };
      }
      return true;
    },
    date: (value, branch, path) => {
      let error = '';
      const date: Date =
        typeof value === 'string'
          ? new Date(
              Date.parse(value) + new Date().getTimezoneOffset() * 60 * 1000
            )
          : value;
      if (typeof value === 'string') {
        const doesNotExist = 'Date does not exist';
        const [year, month, day] = value.split('-');
        const dayA = parseFloat(day);
        const monthA = parseFloat(month);
        const yearA = parseFloat(year);
        const parsedDate = new Date(Date.parse(value));
        const dayB = parsedDate.getUTCDate();
        const monthB = parsedDate.getUTCMonth() + 1;
        const yearB = parsedDate.getUTCFullYear();

        if (!error && value.length === 0) {
          error = doesNotExist;
        }
        if (
          !error &&
          day.length !== 2 &&
          month.length !== 2 &&
          year.length !== 4
        ) {
          error = doesNotExist;
        }
        if ((!error && dayA !== dayB) || monthA !== monthB || yearA !== yearB) {
          error = doesNotExist;
        }
      }
      if (!error && isValid(date) === false) {
        error = 'Please enter a valid date';
      }
      if (!error && isFuture(date)) {
        error = 'Date is in the future';
      }
      if (error) {
        return {
          type: 'date',
          value,
          message: error,
          path,
          branch,
        };
      }
      return true;
    },
  },
});

const dateStruct = structTypes({
  date: 'date',
});

const valueStruct = structTypes({
  value: 'number',
});

const transactionStruct = structTypes({
  date: 'date',
  shares: 'number',
  price: 'number',
  cost: 'number',
});

const types = [
  { option: TRANSACTION_TYPE_BUY, label: TRANSACTION_TYPE_BUY },
  { option: TRANSACTION_TYPE_SELL, label: TRANSACTION_TYPE_SELL },
];

type ValueKey = 'shares' | 'price';

export interface AddTransactionArgs {
  portfolioId: string;
  companyId: string;
  itemId: string;
  type: string;
  date: number;
  shares: string;
  cost: number;
}

export interface Props {
  portfolioId: string;
  itemId: string;
  companyId: string;
  itemPrices: Record<string, number>;
  tickerSymbol: string;
  handleClose: () => void;
  handleAdd: (options: AddTransactionArgs) => void;
  handleFetchPrice: (options: FetchPriceArgs) => void;
  isFetchingPrice: boolean;
}

interface State {
  date: Date | null;
  ISO8601: string;
  shares: string;
  price: string;
  cost: number | null;
  type: string;
  errors: {
    date: string;
    shares: string;
    price: string;
  };
}

@track({
  type: 'track',
})
class AddTransactionsModal extends Component<Props & WithThemeProps, State> {
  constructor(props) {
    super(props);
    this.state = {
      date: null,
      ISO8601: '',
      shares: '',
      price: '',
      cost: null,
      type: TRANSACTION_TYPE_BUY,
      errors: {
        date: '',
        shares: '',
        price: '',
      },
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { itemPrices } = this.props;
    const { itemPrices: prevItemPrices } = prevProps;
    const { date, shares } = this.state;
    const { date: prevDate } = prevState;

    let price = '';
    let cost: number | null = null;

    const nextPrice = get(
      itemPrices,
      [date !== null ? date.getTime().toString() : -1],
      null
    );

    if (!isEqualWith(itemPrices, prevItemPrices) && nextPrice !== null) {
      let error = '';
      price = nextPrice.toString();
      if (shares) {
        cost = +toSignificant(parseFloat(shares) * nextPrice, 2);
      }
      try {
        valueStruct({
          value: price,
        });
      } catch (e: any) {
        const { message } = e;
        error = message;
      } finally {
        this.setState(({ errors }) => ({
          price,
          cost,
          errors: {
            ...errors,
            price: error,
          },
        }));
      }
    }

    if (date !== null && !isEqual(date, prevDate)) {
      if (nextPrice !== null) {
        price = nextPrice.toString();
        if (shares) {
          cost = +toSignificant(parseFloat(shares) * nextPrice, 2);
        }
      }
      this.setState(({ errors }) => ({
        price,
        cost,
        errors: {
          ...errors,
          price: '',
        },
      }));
    }
  }

  handleTypeChange = (value) => {
    this.setState({ type: value });
  };

  validateDate = (ISO8601: string) => {
    const { itemId, companyId, itemPrices, handleFetchPrice } = this.props;
    let error = '';
    let date: Date | null = null;
    try {
      const result = dateStruct({
        date: ISO8601,
      });
      date = new Date(Date.parse(result.date));
    } catch (e: any) {
      const { message } = e;
      error = message;
    } finally {
      this.setState(({ errors }) => ({
        date,
        errors: {
          ...errors,
          date: error,
        },
      }));
      const price = get(
        itemPrices,
        [date !== null ? date.getTime().toString() : -1],
        null
      );
      !error &&
        price === null &&
        date !== null &&
        handleFetchPrice({ itemId, companyId, requestDate: date });
    }
  };

  validateValue = (value: string, key: ValueKey) => {
    const { shares, price } = this.state;
    let cost: number | null = null;
    let error = '';
    try {
      valueStruct({
        value,
      });
      if ((key === 'price' && shares) || (key === 'shares' && price)) {
        cost = +toSignificant(
          parseFloat(value) * parseFloat(key === 'price' ? shares : price),
          2
        );
      }
    } catch (e: any) {
      const { message } = e;
      error = message;
      value = '';
    } finally {
      this.setState((state) => ({
        ...state,
        [key]: value,
        cost,
        errors: {
          ...state.errors,
          [key]: error,
        },
      }));
    }
  };

  handleDateChange = (ISO8601: string) => {
    const { validateDate } = this;
    if (ISO8601.length === 10) {
      validateDate(ISO8601);
      this.setState({
        ISO8601,
      });
    } else {
      this.setState({
        ISO8601,
        date: null,
        price: '',
        cost: null,
      });
    }
  };

  handleSharesChange = (e: FormEvent<HTMLInputElement>) => {
    const { validateValue } = this;
    validateValue(e.currentTarget.value, 'shares');
  };

  handlePriceChange = async (e: FormEvent<HTMLInputElement>) => {
    const { validateValue } = this;
    validateValue(e.currentTarget.value, 'price');
  };

  @track({
    action: 'click',
    modifier: 'add',
  })
  handleSubmit = () => {
    const { portfolioId, companyId, itemId, handleAdd, handleClose } =
      this.props;
    const { type, date, shares, cost, price } = this.state;
    try {
      transactionStruct({
        date,
        shares,
        price,
        cost,
      });
      if (date !== null && cost !== null) {
        handleClose();
        handleAdd({
          portfolioId,
          companyId,
          itemId,
          type,
          date: date.getTime(),
          shares,
          cost,
        });
      }
    } catch (e: any) {
      const { failures } = e;
      this.setState(({ errors }) => ({
        errors: {
          ...errors,
          ...failures.reduce(
            (a, b) => ({
              ...a,
              [b.path[0]]: b.message,
            }),
            {}
          ),
        },
      }));
    }
  };

  render() {
    const {
      handleDateChange,
      handleTypeChange,
      handleSharesChange,
      handlePriceChange,
      handleSubmit,
    } = this;

    const { theme, isFetchingPrice, tickerSymbol, handleClose, itemPrices } =
      this.props;

    const { ISO8601, type, shares, price, cost, errors, date } = this.state;

    const isPriceAvailable = Number(date).toString() in itemPrices;

    return (
      <Modal
        title={`Add Transaction (${tickerSymbol})`}
        hideSecondary={true}
        fullscreen={true}
        primaryLabel="Add"
        onSecondaryClick={handleClose}
        onPrimaryClick={handleSubmit}
      >
        <>
          <FieldWrapper>
            <Media query={`(max-width: ${theme.lgMin})`}>
              {(matches) =>
                matches ? (
                  <Textfield
                    styleType="standardReverse"
                    name="date"
                    type="date"
                    label="Transaction Date"
                    placeholder={`e.g. 2018-01-01"`}
                    value={ISO8601}
                    onChange={(e) => handleDateChange(e.currentTarget.value)}
                    error={errors.date}
                  />
                ) : (
                  <>
                    <DateField
                      name="date"
                      onChange={handleDateChange}
                      error={errors.date}
                    />
                  </>
                )
              }
            </Media>
          </FieldWrapper>
          <FieldWrapper>
            <Dropdown
              styleType="standardReverse"
              label="Type of Holding"
              options={types}
              onSelect={handleTypeChange}
              value={type}
            />
          </FieldWrapper>
          <FieldWrapper>
            <Textfield
              styleType="standardReverse"
              name="shares"
              type="text"
              label="Number of Shares"
              value={shares}
              placeholder="e.g. 100"
              error={errors.shares}
              onChange={handleSharesChange}
            />
          </FieldWrapper>
          <Row>
            <AddTransactionField col={12} colSm={6}>
              <Textfield
                styleType="standardReverse"
                name="price"
                type="text"
                label="Price"
                value={price}
                placeholder="0.00"
                error={
                  errors.price ||
                  (date !== null && !isFetchingPrice && !isPriceAvailable
                    ? 'Price unavailable'
                    : '')
                }
                onChange={handlePriceChange}
                disabled={isFetchingPrice}
              />
            </AddTransactionField>
            <AddTransactionField col={12} colSm={6}>
              <Textfield
                styleType="standardReverse"
                name="cost"
                type="text"
                label="Cost"
                value={
                  cost
                    ? cost.toLocaleString
                      ? cost.toLocaleString().toString()
                      : precisionRound(cost, 2).toString()
                    : ''
                }
                disabled={true}
              />
            </AddTransactionField>
          </Row>
        </>
      </Modal>
    );
  }
}

export default withTheme(AddTransactionsModal);
